import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  Plus, 
  Search, 
  Filter, 
  Download, 
  Edit, 
  Trash2, 
  Eye, 
  Mail, 
  Phone, 
  Calendar, 
  Award, 
  BookOpen, 
  Target, 
  Users, 
  Building2, 
  GraduationCap,
  CheckCircle2,
  Clock,
  AlertCircle,
  X,
  ChevronRight,
  UserPlus,
  FileText,
  BarChart2
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

// Mock data
const mockData = {
  learners: [
    {
      id: 1,
      name: "Ahmed Al-Rashid",
      email: "ahmed@company.com",
      phone: "+966 50 123 4567",
      department: "Kitchen",
      position: "Chef",
      hireDate: "2023-01-15",
      enrolledCourses: 6,
      completedCourses: 4,
      avgScore: 87,
      certificates: 4,
      lastActive: "2024-03-15",
      status: "active",
      progress: [
        { course: "Food Safety Fundamentals", progress: 100, score: 92 },
        { course: "Customer Service Excellence", progress: 100, score: 88 },
        { course: "Inventory Management", progress: 75, score: null },
        { course: "Leadership Skills", progress: 25, score: null }
      ]
    },
    {
      id: 2,
      name: "Fatima Hassan",
      email: "fatima@company.com",
      phone: "+966 55 987 6543",
      department: "Service",
      position: "Team Lead",
      hireDate: "2022-08-20",
      enrolledCourses: 8,
      completedCourses: 6,
      avgScore: 92,
      certificates: 6,
      lastActive: "2024-03-15",
      status: "active",
      progress: [
        { course: "Food Safety Fundamentals", progress: 100, score: 95 },
        { course: "Customer Service Excellence", progress: 100, score: 90 },
        { course: "Inventory Management", progress: 100, score: 88 },
        { course: "Leadership Skills", progress: 60, score: null }
      ]
    },
    {
      id: 3,
      name: "Omar Khalil",
      email: "omar@company.com",
      phone: "+966 54 456 7890",
      department: "Management",
      position: "Manager",
      hireDate: "2021-12-10",
      enrolledCourses: 12,
      completedCourses: 10,
      avgScore: 89,
      certificates: 10,
      lastActive: "2024-03-14",
      status: "active",
      progress: [
        { course: "Food Safety Fundamentals", progress: 100, score: 85 },
        { course: "Customer Service Excellence", progress: 100, score: 92 },
        { course: "Inventory Management", progress: 100, score: 90 },
        { course: "Leadership Skills", progress: 100, score: 88 }
      ]
    },
    {
      id: 4,
      name: "Layla Al-Mansouri",
      email: "layla@company.com",
      phone: "+966 56 789 0123",
      department: "Kitchen",
      position: "Sous Chef",
      hireDate: "2023-06-05",
      enrolledCourses: 4,
      completedCourses: 2,
      avgScore: 78,
      certificates: 2,
      lastActive: "2024-03-10",
      status: "active",
      progress: [
        { course: "Food Safety Fundamentals", progress: 100, score: 80 },
        { course: "Customer Service Excellence", progress: 100, score: 76 },
        { course: "Inventory Management", progress: 30, score: null },
        { course: "Leadership Skills", progress: 0, score: null }
      ]
    }
  ],
  departments: [
    { id: 1, name: "Kitchen" },
    { id: 2, name: "Service" },
    { id: 3, name: "Management" },
    { id: 4, name: "HR" },
    { id: 5, name: "IT" }
  ],
  courses: [
    { id: 1, name: "Food Safety Fundamentals" },
    { id: 2, name: "Customer Service Excellence" },
    { id: 3, name: "Inventory Management" },
    { id: 4, name: "Leadership Skills" }
  ]
};

const LearnerManagement = () => {
  const navigate = useNavigate();
  const [learners, setLearners] = useState(mockData.learners);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDepartment, setFilterDepartment] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedLearner, setSelectedLearner] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  const tabs = [
    { id: 'overview', label: 'Overview', icon: BarChart2 },
    { id: 'learners', label: 'Learners', icon: Users },
    { id: 'analytics', label: 'Analytics', icon: Target }
  ];

  // Filter learners based on search and filters
  const filteredLearners = learners.filter(learner => {
    const matchesSearch = learner.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         learner.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         learner.department.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesDepartment = filterDepartment === 'all' || learner.department === filterDepartment;
    const matchesStatus = filterStatus === 'all' || learner.status === filterStatus;
    return matchesSearch && matchesDepartment && matchesStatus;
  });

  const handleAddLearner = (learnerData) => {
    const newLearner = {
      id: Date.now(),
      ...learnerData,
      enrolledCourses: 0,
      completedCourses: 0,
      avgScore: 0,
      certificates: 0,
      lastActive: new Date().toISOString().split('T')[0],
      status: "active",
      progress: []
    };
    setLearners([...learners, newLearner]);
    setShowAddModal(false);
  };

  const handleDeleteLearner = (learnerId) => {
    if (window.confirm('Are you sure you want to delete this learner?')) {
      setLearners(learners.filter(learner => learner.id !== learnerId));
    }
  };

  const handleViewProfile = (learner) => {
    setSelectedLearner(learner);
    setShowProfileModal(true);
  };

  const OverviewContent = () => {
    const stats = {
      totalLearners: learners.length,
      activeLearners: learners.filter(l => l.status === 'active').length,
      avgCompletionRate: Math.round(learners.reduce((acc, l) => acc + (l.completedCourses / l.enrolledCourses * 100), 0) / learners.length),
      avgScore: Math.round(learners.reduce((acc, l) => acc + l.avgScore, 0) / learners.length)
    };

    const departmentStats = mockData.departments.map(dept => ({
      department: dept.name,
      learners: learners.filter(l => l.department === dept.name).length,
      avgScore: Math.round(learners.filter(l => l.department === dept.name).reduce((acc, l) => acc + l.avgScore, 0) / learners.filter(l => l.department === dept.name).length) || 0
    }));

    const progressData = [
      { month: 'Jan', enrollments: 12, completions: 8 },
      { month: 'Feb', enrollments: 15, completions: 12 },
      { month: 'Mar', enrollments: 18, completions: 15 },
      { month: 'Apr', enrollments: 22, completions: 18 },
      { month: 'May', enrollments: 25, completions: 20 },
      { month: 'Jun', enrollments: 28, completions: 24 }
    ];

    return (
      <div className="space-y-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <motion.div
            whileHover={{ scale: 1.02 }}
            className="bg-white rounded-xl p-6 shadow-sm"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Learners</p>
                <h3 className="text-2xl font-bold text-gray-800 mt-1">{stats.totalLearners}</h3>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </motion.div>

          <motion.div
            whileHover={{ scale: 1.02 }}
            className="bg-white rounded-xl p-6 shadow-sm"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Active Learners</p>
                <h3 className="text-2xl font-bold text-gray-800 mt-1">{stats.activeLearners}</h3>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                <CheckCircle2 className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </motion.div>

          <motion.div
            whileHover={{ scale: 1.02 }}
            className="bg-white rounded-xl p-6 shadow-sm"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Avg Completion Rate</p>
                <h3 className="text-2xl font-bold text-gray-800 mt-1">{stats.avgCompletionRate}%</h3>
              </div>
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                <Target className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </motion.div>

          <motion.div
            whileHover={{ scale: 1.02 }}
            className="bg-white rounded-xl p-6 shadow-sm"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Average Score</p>
                <h3 className="text-2xl font-bold text-gray-800 mt-1">{stats.avgScore}%</h3>
              </div>
              <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                <Award className="w-6 h-6 text-amber-600" />
              </div>
            </div>
          </motion.div>
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl p-6 shadow-sm"
          >
            <h3 className="text-lg font-semibold mb-4">Enrollment Progress</h3>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={progressData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Line type="monotone" dataKey="enrollments" stroke="#8B5CF6" strokeWidth={2} />
                <Line type="monotone" dataKey="completions" stroke="#10B981" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl p-6 shadow-sm"
          >
            <h3 className="text-lg font-semibold mb-4">Department Performance</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={departmentStats}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="department" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="avgScore" fill="#8B5CF6" />
              </BarChart>
            </ResponsiveContainer>
          </motion.div>
        </div>
      </div>
    );
  };

  const LearnersContent = () => (
    <div className="space-y-6">
      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search learners..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>
        </div>
        <select
          value={filterDepartment}
          onChange={(e) => setFilterDepartment(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
        >
          <option value="all">All Departments</option>
          {mockData.departments.map(dept => (
            <option key={dept.id} value={dept.name}>{dept.name}</option>
          ))}
        </select>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
        >
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
        >
          <UserPlus className="w-4 h-4" />
          Add Learner
        </button>
      </div>

      {/* Learners Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredLearners.map((learner) => (
          <motion.div
            key={learner.id}
            whileHover={{ scale: 1.02 }}
            className="bg-white rounded-xl p-6 shadow-sm hover:shadow-lg transition-all cursor-pointer"
            onClick={() => handleViewProfile(learner)}
          >
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-800">{learner.name}</h3>
                <p className="text-sm text-gray-500">{learner.department} • {learner.position}</p>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400" />
            </div>
            
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <p className="text-sm text-gray-500">Courses</p>
                <p className="font-medium">{learner.enrolledCourses} enrolled, {learner.completedCourses} completed</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Certificates</p>
                <p className="font-medium">{learner.certificates}</p>
              </div>
            </div>
            
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-500">Avg Score: {learner.avgScore}%</span>
              <span className="text-xs text-gray-400">Last active: {learner.lastActive}</span>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );

  const AnalyticsContent = () => {
    const topPerformers = learners
      .sort((a, b) => b.avgScore - a.avgScore)
      .slice(0, 5);

    const departmentBreakdown = mockData.departments.map(dept => ({
      department: dept.name,
      count: learners.filter(l => l.department === dept.name).length,
      avgScore: Math.round(learners.filter(l => l.department === dept.name).reduce((acc, l) => acc + l.avgScore, 0) / learners.filter(l => l.department === dept.name).length) || 0
    }));

    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl p-6 shadow-sm"
          >
            <h3 className="text-lg font-semibold mb-4">Top Performers</h3>
            <div className="space-y-4">
              {topPerformers.map((learner, index) => (
                <div key={learner.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-gradient-to-r from-amber-400 to-orange-500 rounded-full flex items-center justify-center text-white font-bold">
                      {index + 1}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{learner.name}</p>
                      <p className="text-sm text-gray-500">{learner.department}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-medium text-gray-900">{learner.avgScore}%</p>
                    <p className="text-sm text-gray-500">{learner.completedCourses} courses</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl p-6 shadow-sm"
          >
            <h3 className="text-lg font-semibold mb-4">Department Breakdown</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={departmentBreakdown}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="department" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="count" fill="#8B5CF6" />
              </BarChart>
            </ResponsiveContainer>
          </motion.div>
        </div>
      </div>
    );
  };

  const AddLearnerModal = ({ isOpen, onClose, onAdd }) => {
    const [formData, setFormData] = useState({
      name: '',
      email: '',
      phone: '',
      department: '',
      position: '',
      hireDate: ''
    });

    const handleSubmit = (e) => {
      e.preventDefault();
      onAdd(formData);
      setFormData({
        name: '',
        email: '',
        phone: '',
        department: '',
        position: '',
        hireDate: ''
      });
    };

    if (!isOpen) return null;

    return (
      <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm z-50 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-xl w-full max-w-md shadow-xl m-4"
        >
          <div className="p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Add New Learner</h3>
              <button
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Department *
                </label>
                <select
                  value={formData.department}
                  onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  required
                >
                  <option value="">Select department</option>
                  {mockData.departments.map(dept => (
                    <option key={dept.id} value={dept.name}>{dept.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Position
                </label>
                <input
                  type="text"
                  value={formData.position}
                  onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Hire Date
                </label>
                <input
                  type="date"
                  value={formData.hireDate}
                  onChange={(e) => setFormData({ ...formData, hireDate: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                >
                  Add Learner
                </button>
              </div>
            </form>
          </div>
        </motion.div>
      </div>
    );
  };

  const LearnerProfileModal = ({ isOpen, onClose, learner }) => {
    if (!isOpen || !learner) return null;

    return (
      <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm z-50 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto shadow-xl m-4"
        >
          <div className="p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-semibold text-gray-900">Learner Profile</h3>
              <button
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Basic Info */}
              <div className="lg:col-span-1">
                <div className="bg-gray-50 rounded-lg p-6">
                  <h4 className="text-lg font-semibold mb-4">Basic Information</h4>
                  <div className="space-y-3">
                    <div>
                      <p className="text-sm text-gray-500">Name</p>
                      <p className="font-medium">{learner.name}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Email</p>
                      <p className="font-medium">{learner.email}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Phone</p>
                      <p className="font-medium">{learner.phone}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Department</p>
                      <p className="font-medium">{learner.department}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Position</p>
                      <p className="font-medium">{learner.position}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Hire Date</p>
                      <p className="font-medium">{learner.hireDate}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Progress */}
              <div className="lg:col-span-2">
                <div className="bg-gray-50 rounded-lg p-6">
                  <h4 className="text-lg font-semibold mb-4">Course Progress</h4>
                  <div className="space-y-4">
                    {learner.progress.map((course, index) => (
                      <div key={index} className="bg-white rounded-lg p-4">
                        <div className="flex justify-between items-center mb-2">
                          <h5 className="font-medium">{course.course}</h5>
                          <span className="text-sm text-gray-500">{course.progress}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-purple-600 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${course.progress}%` }}
                          ></div>
                        </div>
                        {course.score && (
                          <p className="text-sm text-gray-600 mt-1">Score: {course.score}%</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  onClose();
                  // Navigate to edit page
                }}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Edit className="w-4 h-4" />
                Edit Profile
              </button>
              <button
                onClick={() => handleDeleteLearner(learner.id)}
                className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                <Trash2 className="w-4 h-4" />
                Delete Learner
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    );
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return <OverviewContent />;
      case 'learners':
        return <LearnersContent />;
      case 'analytics':
        return <AnalyticsContent />;
      default:
        return <OverviewContent />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-4 mb-4">
          <Link
            to="/bloom"
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Bloom
          </Link>
        </div>
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Learner Management</h1>
            <p className="text-gray-500">Manage learners, track progress, and view analytics</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => alert('Exporting learner data...')}
              className="flex items-center gap-2 px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <Download className="w-4 h-4" />
              Export
            </button>
            <button
              onClick={() => setShowAddModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              <UserPlus className="w-4 h-4" />
              Add Learner
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl shadow-sm mb-6">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab.id
                    ? 'border-purple-500 text-purple-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Content */}
      <div className="space-y-6">
        <AnimatePresence mode="wait">
          {renderTabContent()}
        </AnimatePresence>
      </div>

      {/* Modals */}
      <AddLearnerModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onAdd={handleAddLearner}
      />

      <LearnerProfileModal
        isOpen={showProfileModal}
        onClose={() => setShowProfileModal(false)}
        learner={selectedLearner}
      />
    </div>
  );
};

export default LearnerManagement;
